import React, { useCallback, useMemo, useState } from "react";
// i18n
import { useTranslation } from "react-i18next";

// Context
import { useCoursesNewContext } from "../../context";

// Components
import Button from "../../../../../components/button";

// Styles
import styles from "./style.module.scss";
import stepsStyles from "../steps.module.scss";

// Types
import { CourseData } from "../../types";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import CustomButton from "../../../../../components/CustomButton";

interface EducationLevelProps {
  onNext: (courseData: CourseData | null) => void;
  onPrev: () => void;
}

const EducationLevel: React.FC<EducationLevelProps> = ({ onNext, onPrev }) => {
  // Translations
  const { t } = useTranslation();

  // Context
  const context = useCoursesNewContext();

  // State
  const [educationLevel, setEducationLevel] = useState<string>(context.course?.education_level || "");
  const [schoolName, setSchoolName] = useState<string>(context.course?.school_name || "");
  const [errors, setErrors] = useState<{ educationLevel?: boolean, schoolName?: boolean }>({
    educationLevel: false,
    schoolName: false,
  });

  // Memoized
  const educationLevelOptions = useMemo(() => {
    return [
      {
        label: t('courses.new.education_level.education_level_options.middle_school'),
        value: 'middle_school',
      },
      {
        label: t('courses.new.education_level.education_level_options.high_school'),
        value: 'high_school',
      },

      {
        label: t('courses.new.education_level.education_level_options.university_college'),
        value: 'university_college',
      },
      {
        label: t('courses.new.education_level.education_level_options.graduate_phd'),
        value: 'graduate_phd',
      },
      {
        label: t('courses.new.education_level.education_level_options.continuous_learning'),
        value: 'continuous_learning',
      },
    ];
  }, [t]);

  // Handlers
  const handleChangeEducationLevel = useCallback((value: string) => {
    setEducationLevel(value);
    setErrors({
      educationLevel: false,
    });
  }, []);

  const handleChangeSchoolName = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSchoolName(e.target.value);
    setErrors({
      schoolName: false,
    });
  }, []);

  const handleNext = useCallback(async () => {
    if (educationLevel !== '') {
      let _course = { ...context.course, education_level: educationLevel, school_name: schoolName };

      if (!!_course.id) {
        const result = await context.updateCourse(_course);
        if (result.success) onNext(result.data);
      } else onNext(_course);
    } else {
      setErrors({ educationLevel: true });
    }
  }, [onNext, educationLevel, schoolName]);

  const handlePrev = useCallback(() => {
    onPrev();
  }, [onPrev]);

  // Render

  return (
    <div className={stepsStyles.root}>
      <div className={stepsStyles.content}>
        <h1 className='text-2xl font-bold text-black dark:text-white'>
          {t('courses.new.education_level.title')}
        </h1>

        <div className={styles.formItem}>
          <label className='text-sm font-medium text-black dark:text-white'>
            {t('courses.new.education_level.education_level_label')}
            <span className='text-blue-500'>*</span>
          </label>

          <Select
            value={educationLevel}
            onChange={(e) => handleChangeEducationLevel(e.target.value)}
            classes={{ root: styles.select }}
            required={true}
          >
            {educationLevelOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </div>


        <div className={styles.formItem}>
          <label className={stepsStyles.label}>
            {t('courses.new.education_level.school_name_label')}
          </label>

          <TextField
            value={schoolName}
            onChange={handleChangeSchoolName}
            classes={{ root: styles.input }}
            required={true}
          />
        </div>
      </div>

      <div className={stepsStyles.actions}>
        <CustomButton
          variant="secondary"
          onClick={handlePrev}
          classes={{ root: stepsStyles.prevButton }}
        >
          {t('actions.previous')}
        </CustomButton>

        <CustomButton
          variant="primary"
          onClick={handleNext}
          classes={{ root: stepsStyles.nextButton }}
          disabled={errors.educationLevel || educationLevel === ''}
        >
          {t('actions.next')}
        </CustomButton>
      </div>
    </div>
  );
};

export default EducationLevel;
